import React, { useMemo } from 'react';
import rehypeRaw from 'rehype-raw';
import MarkdownRenderer from '../ui/MarkdownRenderer';
import CitationHighlight from './CitationHighlight';
import EntityHighlight from './EntityHighlight';

const REHYPE_PLUGINS = [rehypeRaw];

/**
 * Renders assistant reply markdown (tables, lists, raw HTML) with optional
 * citation/entity highlighting. Shared by the main chat and the workflow chat.
 *
 * @param {string} children Markdown source to render.
 * @param {object} [citeById] Map of citation id -> source (for <mark data-cite>).
 * @param {object} [entityById] Map of entity id -> entity (for <mark data-entity>).
 * @param {*} [toolResults] Passed through when opening an entity.
 * @param {Function} [onSelectSource] Called with a source when a citation is clicked.
 * @param {Function} [onSelectEntity] Called with `{ entity, results }` when an entity is clicked.
 */
const MessageContent = ({
  children,
  citeById = {},
  entityById = {},
  toolResults,
  onSelectSource,
  onSelectEntity,
}) => {
  const components = useMemo(
    () => ({
      mark: ({
        'data-cite': citeId,
        'data-entity': entityId,
        dataCite,
        dataEntity,
        children: markChildren,
      }) => {
        if (entityId != null || dataEntity != null) {
          return (
            <EntityHighlight
              entity={entityById[entityId ?? dataEntity]}
              onOpen={entity => onSelectEntity?.({ entity, results: toolResults })}>
              {markChildren}
            </EntityHighlight>
          );
        }
        return (
          <CitationHighlight source={citeById[citeId ?? dataCite]} onOpen={onSelectSource}>
            {markChildren}
          </CitationHighlight>
        );
      },
    }),
    [citeById, entityById, toolResults, onSelectSource, onSelectEntity],
  );

  return (
    <MarkdownRenderer rehypePlugins={REHYPE_PLUGINS} components={components}>
      {children}
    </MarkdownRenderer>
  );
};

export default MessageContent;
